/**
 * Phase 138I — Honors & Accolades Ledger (Structured CRUD UI)
 *
 * Academy owner's dashboard surface for logging explicit tournament
 * results + scouting placements. Records POST to:
 *   /api/academy/honors/tournament  (4 endpoints — POST/GET/PUT/DELETE)
 *   /api/academy/honors/placement   (4 endpoints — POST/GET/PUT/DELETE)
 *
 * Public `/api/academies/public/{slug}/honors-hub` unions these with the
 * legacy regex-derived `wall_milestone` records so historical entries
 * stay live during the transition.
 */
import { useEffect, useState, useCallback } from 'react';
import {
  Award, UserCheck, Plus, Loader2, Trash2, Edit3, X, Check,
  AlertCircle, Calendar, Trophy, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import api from '@/lib/api';
// Phase 248 — Shared form hydrators (controlled/uncontrolled input safety net).
import { hydrateTournamentForm, hydratePlacementForm } from '@/utils/formHydrators';

const RANK_OPTIONS = [
  { value: 'Gold', label: '🥇 Gold / Champion', accent: 'amber' },
  { value: 'Silver', label: '🥈 Silver / Runner-Up', accent: 'slate' },
  { value: 'Podium', label: '🥉 Podium Finish', accent: 'orange' },
];

const RANK_BADGE = {
  Gold: { bg: 'bg-amber-100', text: 'text-amber-900', border: 'border-amber-200' },
  Silver: { bg: 'bg-slate-100', text: 'text-slate-800', border: 'border-slate-200' },
  Podium: { bg: 'bg-orange-100', text: 'text-orange-900', border: 'border-orange-200' },
};

const EMPTY_TOURNAMENT = {
  tournament_name: '',
  year: new Date().getFullYear(),
  rank: 'Gold',
  division: '',
  summary: '',
};
const EMPTY_PLACEMENT = {
  athlete_name: '',
  destination_club: '',
  signing_date: new Date().toISOString().slice(0, 10),
  notes: '',
  student_id: '',
};

export default function HonorsManager() {
  const [activeTab, setActiveTab] = useState('tournaments'); // 'tournaments' | 'placements'
  const [tournaments, setTournaments] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);

  const [tournamentForm, setTournamentForm] = useState(EMPTY_TOURNAMENT);
  const [placementForm, setPlacementForm] = useState(EMPTY_PLACEMENT);
  const [editingTournamentId, setEditingTournamentId] = useState(null);
  const [editingPlacementId, setEditingPlacementId] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, pRes] = await Promise.all([
        api.get('/academy/honors/tournament'),
        api.get('/academy/honors/placement'),
      ]);
      setTournaments(tRes.data.items || []);
      setPlacements(pRes.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load honors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // === Tournament handlers ===
  const submitTournament = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingTournamentId) {
        await api.put(`/academy/honors/tournament/${editingTournamentId}`, tournamentForm);
        toast.success('Tournament updated');
      } else {
        await api.post('/academy/honors/tournament', tournamentForm);
        toast.success('Tournament logged');
      }
      setTournamentForm(EMPTY_TOURNAMENT);
      setEditingTournamentId(null);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const editTournament = (t) => {
    setTournamentForm(hydrateTournamentForm(t));
    setEditingTournamentId(t.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteTournament = async (id) => {
    if (!window.confirm('Delete this tournament record? This cannot be undone.')) return;
    try {
      await api.delete(`/academy/honors/tournament/${id}`);
      toast.success('Tournament deleted');
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const cancelTournamentEdit = () => {
    setTournamentForm(EMPTY_TOURNAMENT);
    setEditingTournamentId(null);
  };

  // === Placement handlers ===
  const submitPlacement = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...placementForm };
      if (!payload.student_id) delete payload.student_id;
      if (editingPlacementId) {
        await api.put(`/academy/honors/placement/${editingPlacementId}`, payload);
        toast.success('Signing updated');
      } else {
        await api.post('/academy/honors/placement', payload);
        toast.success('Signing logged');
      }
      setPlacementForm(EMPTY_PLACEMENT);
      setEditingPlacementId(null);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const editPlacement = (p) => {
    setPlacementForm(hydratePlacementForm(p));
    setEditingPlacementId(p.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deletePlacement = async (id) => {
    if (!window.confirm('Delete this signing record? This cannot be undone.')) return;
    try {
      await api.delete(`/academy/honors/placement/${id}`);
      toast.success('Signing deleted');
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const cancelPlacementEdit = () => {
    setPlacementForm(EMPTY_PLACEMENT);
    setEditingPlacementId(null);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 lg:p-8" data-testid="honors-manager-page">
      <Toaster richColors position="top-center" />

      {/* Header */}
      <div className="mb-6 max-w-6xl mx-auto">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-100 text-amber-900 text-[10px] font-extrabold uppercase tracking-wider mb-2">
              <Trophy className="w-3 h-3" /> Phase 138I · Honors Ledger
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
              Honors & Accolades Ledger
            </h1>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl leading-relaxed">
              Log structured tournament wins and pro signings — they go live on your public Wall of Champions instantly. Replaces free-text milestones with verified, schema-validated records.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto mb-5">
        <div className="inline-flex bg-white border border-slate-200 rounded-xl p-1 shadow-sm" role="tablist">
          <button
            onClick={() => setActiveTab('tournaments')}
            data-testid="honors-tab-tournaments"
            className={`px-4 py-2 text-xs font-bold rounded-lg flex items-center gap-1.5 transition-all ${
              activeTab === 'tournaments'
                ? 'bg-slate-900 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Award className="w-3.5 h-3.5" />
            Tournament Results
            <span className="ml-1 px-1.5 py-0.5 text-[9px] font-extrabold rounded bg-slate-200 text-slate-700">
              {tournaments.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('placements')}
            data-testid="honors-tab-placements"
            className={`px-4 py-2 text-xs font-bold rounded-lg flex items-center gap-1.5 transition-all ${
              activeTab === 'placements'
                ? 'bg-slate-900 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <UserCheck className="w-3.5 h-3.5" />
            Pro Signings
            <span className="ml-1 px-1.5 py-0.5 text-[9px] font-extrabold rounded bg-slate-200 text-slate-700">
              {placements.length}
            </span>
          </button>
        </div>
      </div>

      {activeTab === 'tournaments' && (
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Form Card */}
          <form
            onSubmit={submitTournament}
            className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4 h-fit"
            data-testid="honors-tournament-form"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
                <Award className="w-4 h-4 text-amber-600" />
                {editingTournamentId ? 'Edit Tournament' : 'Log Tournament Result'}
              </h3>
              {editingTournamentId && (
                <button
                  type="button"
                  onClick={cancelTournamentEdit}
                  className="text-[11px] text-slate-500 hover:text-slate-900 flex items-center gap-1"
                  data-testid="honors-tournament-cancel-edit"
                >
                  <X className="w-3 h-3" /> Cancel
                </button>
              )}
            </div>

            <Field label="Tournament Title" required>
              <input
                type="text" required maxLength={140}
                value={tournamentForm.tournament_name}
                onChange={(e) => setTournamentForm({ ...tournamentForm, tournament_name: e.target.value })}
                placeholder="e.g., State Youth Championship 2026"
                className="w-full text-sm px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-amber-300 focus:ring-2 focus:ring-amber-100 outline-none transition"
                data-testid="honors-tournament-name-input"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Year" required>
                <input
                  type="number" required min={1990} max={2099}
                  value={tournamentForm.year}
                  onChange={(e) => setTournamentForm({ ...tournamentForm, year: parseInt(e.target.value) || new Date().getFullYear() })}
                  className="w-full text-sm px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-amber-300 outline-none"
                  data-testid="honors-tournament-year-input"
                />
              </Field>
              <Field label="Placement" required>
                <select
                  value={tournamentForm.rank}
                  onChange={(e) => setTournamentForm({ ...tournamentForm, rank: e.target.value })}
                  className="w-full text-sm px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white outline-none"
                  data-testid="honors-tournament-rank-input"
                >
                  {RANK_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Division / Category" required>
              <input
                type="text" required maxLength={80}
                value={tournamentForm.division}
                onChange={(e) => setTournamentForm({ ...tournamentForm, division: e.target.value })}
                placeholder="e.g., Under-17 Elite Boys"
                className="w-full text-sm px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white outline-none"
                data-testid="honors-tournament-division-input"
              />
            </Field>

            <Field label="Summary (optional)">
              <textarea
                rows={3} maxLength={600}
                value={tournamentForm.summary}
                onChange={(e) => setTournamentForm({ ...tournamentForm, summary: e.target.value })}
                placeholder="Brief context — final score, opponent, MVP, etc."
                className="w-full text-sm px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white outline-none resize-none"
                data-testid="honors-tournament-summary-input"
              />
            </Field>

            <button
              type="submit"
              disabled={saving}
              className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white font-bold text-xs rounded-lg transition-colors flex items-center justify-center gap-2"
              data-testid="honors-tournament-submit-btn"
            >
              {saving ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>
              ) : editingTournamentId ? (
                <><Check className="w-3.5 h-3.5" /> Save Changes</>
              ) : (
                <><Plus className="w-3.5 h-3.5" /> Add to Public Wall</>
              )}
            </button>
            <p className="text-[10px] text-slate-400 text-center">
              Records appear instantly on your public Honors Hub.
            </p>
          </form>

          {/* List Card */}
          <div className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider">
                Logged Tournaments
              </h3>
              <span className="text-[10px] text-slate-500 font-bold">
                {tournaments.length} {tournaments.length === 1 ? 'record' : 'records'}
              </span>
            </div>
            {loading ? (
              <EmptyHint icon={Loader2} text="Loading…" spinning />
            ) : tournaments.length === 0 ? (
              <EmptyHint
                icon={AlertCircle}
                text="No tournament records yet. Log your first podium win using the form."
              />
            ) : (
              <div className="space-y-2.5">
                {tournaments.map((t) => (
                  <RowCard
                    key={t.id}
                    accent={RANK_BADGE[t.rank]}
                    badge={t.rank}
                    chip={String(t.year)}
                    title={t.tournament_name}
                    subtitle={t.division}
                    body={t.summary}
                    onEdit={() => editTournament(t)}
                    onDelete={() => deleteTournament(t.id)}
                    editTestId="honors-tournament-edit"
                    deleteTestId="honors-tournament-delete"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'placements' && (
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Form Card */}
          <form
            onSubmit={submitPlacement}
            className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4 h-fit"
            data-testid="honors-placement-form"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-indigo-600" />
                {editingPlacementId ? 'Edit Signing' : 'Log Pro Signing'}
              </h3>
              {editingPlacementId && (
                <button
                  type="button"
                  onClick={cancelPlacementEdit}
                  className="text-[11px] text-slate-500 hover:text-slate-900 flex items-center gap-1"
                  data-testid="honors-placement-cancel-edit"
                >
                  <X className="w-3 h-3" /> Cancel
                </button>
              )}
            </div>

            <Field label="Athlete Name" required>
              <input
                type="text" required maxLength={100}
                value={placementForm.athlete_name}
                onChange={(e) => setPlacementForm({ ...placementForm, athlete_name: e.target.value })}
                placeholder="e.g., Aarav Mehta"
                className="w-full text-sm px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 outline-none transition"
                data-testid="honors-placement-name-input"
              />
            </Field>

            <Field label="Destination Club / Programme" required>
              <input
                type="text" required maxLength={120}
                value={placementForm.destination_club}
                onChange={(e) => setPlacementForm({ ...placementForm, destination_club: e.target.value })}
                placeholder="e.g., Bengaluru FC U-18 Academy"
                className="w-full text-sm px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-indigo-300 outline-none"
                data-testid="honors-placement-club-input"
              />
            </Field>

            <Field label="Signing Date" required>
              <input
                type="date" required
                value={placementForm.signing_date}
                onChange={(e) => setPlacementForm({ ...placementForm, signing_date: e.target.value })}
                className="w-full text-sm px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white outline-none"
                data-testid="honors-placement-date-input"
              />
            </Field>

            <Field label="Notes (optional)">
              <textarea
                rows={3} maxLength={600}
                value={placementForm.notes}
                onChange={(e) => setPlacementForm({ ...placementForm, notes: e.target.value })}
                placeholder="Scouting trail, trial outcome, scholarship details…"
                className="w-full text-sm px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white outline-none resize-none"
                data-testid="honors-placement-notes-input"
              />
            </Field>

            <button
              type="submit"
              disabled={saving}
              className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white font-bold text-xs rounded-lg transition-colors flex items-center justify-center gap-2"
              data-testid="honors-placement-submit-btn"
            >
              {saving ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>
              ) : editingPlacementId ? (
                <><Check className="w-3.5 h-3.5" /> Save Changes</>
              ) : (
                <><Plus className="w-3.5 h-3.5" /> Add Signing</>
              )}
            </button>
          </form>

          {/* List Card */}
          <div className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider">
                Logged Signings
              </h3>
              <span className="text-[10px] text-slate-500 font-bold">
                {placements.length} {placements.length === 1 ? 'record' : 'records'}
              </span>
            </div>
            {loading ? (
              <EmptyHint icon={Loader2} text="Loading…" spinning />
            ) : placements.length === 0 ? (
              <EmptyHint
                icon={Users}
                text="No signings logged yet. Record an athlete's pro pathway transition above."
              />
            ) : (
              <div className="space-y-2.5">
                {placements.map((p) => (
                  <RowCard
                    key={p.id}
                    accent={{ bg: 'bg-indigo-100', text: 'text-indigo-900', border: 'border-indigo-200' }}
                    badge="Signing"
                    chip={p.signing_date}
                    title={p.athlete_name}
                    subtitle={`→ ${p.destination_club}`}
                    body={p.notes}
                    onEdit={() => editPlacement(p)}
                    onDelete={() => deletePlacement(p.id)}
                    editTestId="honors-placement-edit"
                    deleteTestId="honors-placement-delete"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] font-extrabold uppercase tracking-wider text-slate-600 block">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
    </div>
  );
}

function EmptyHint({ icon: Icon, text, spinning }) {
  return (
    <div className="text-center py-8">
      <Icon className={`w-7 h-7 text-slate-300 mx-auto mb-2 ${spinning ? 'animate-spin' : ''}`} />
      <p className="text-xs text-slate-500 max-w-xs mx-auto leading-relaxed">{text}</p>
    </div>
  );
}

function RowCard({ accent, badge, chip, title, subtitle, body, onEdit, onDelete, editTestId, deleteTestId }) {
  return (
    <div className="group p-3.5 border border-slate-100 hover:border-slate-200 rounded-xl flex items-start justify-between gap-3 transition">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className={`px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider rounded ${accent.bg} ${accent.text} ${accent.border} border`}>
            {badge}
          </span>
          {chip && (
            <span className="flex items-center gap-1 text-[10px] text-slate-500 font-bold">
              <Calendar className="w-2.5 h-2.5" /> {chip}
            </span>
          )}
        </div>
        <h4 className="text-sm font-bold text-slate-900 truncate">{title}</h4>
        {subtitle && (
          <p className="text-[11px] text-slate-500 font-semibold mt-0.5 truncate">{subtitle}</p>
        )}
        {body && (
          <p className="text-[11px] text-slate-400 mt-1.5 line-clamp-2">{body}</p>
        )}
      </div>
      <div className="flex flex-col gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
        <button
          onClick={onEdit}
          data-testid={editTestId}
          className="p-1.5 hover:bg-slate-100 rounded text-slate-600 hover:text-slate-900 transition"
          title="Edit"
        >
          <Edit3 className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onDelete}
          data-testid={deleteTestId}
          className="p-1.5 hover:bg-rose-50 rounded text-rose-500 hover:text-rose-700 transition"
          title="Delete"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
